import {
  AssertionResource,
  AttestationResource,
  EventsResource,
  GameResource,
  SystemAccountsResource,
  TokenResource,
  TransactionRequestsResource,
  TransactionRequestResponsesResource,
  TransactionsResource,
  UserAccountResource,
  UserResource } from '@monocle-money/api/resources/v1';
import { AttestationResourceClient } from '@monocle-money/api/resources/v1/Auth/Attestation/client';
import { AssertionResourceClient } from '@monocle-money/api/resources/v1/Auth/Assertion/client';
import { GrantType } from '@monocle-money/api/resources/v1/Auth/Token/schema';


import { CBOR_HANDLER, CustomContentTypes } from './content-types';
import { match, P } from 'ts-pattern';
import { ResourceClient, Headers as HeadersEnum, Resource, type ResourceClientInstance, HttpStatusCodes } from '@resourceful-hono/core';
import { TokenManager } from './token-manager';
import { PLAYER_DEFAULT_TOKEN_SCOPE, TOKEN_KEY } from './constants';
import { QueryClient } from '@tanstack/react-query';
import { AppExternalStore } from './app-external-store';
import { FetchBuilder } from './fetch-builder';
import { settle } from '@monocle-money/utils';
import { AvatarResource } from '@monocle-money/api/resources/v1/Avatar';

const appExternalStore = AppExternalStore.instance;
ResourceClient.contentTypes.use(CustomContentTypes.Cbor, CBOR_HANDLER);

const Fetch = new FetchBuilder()
  .with((init) => {
    const tokenManager = TokenManager.instance;
    // reformat headers as Record<string, string>
    const headers = match(init.headers ?? {})
      .with(
        P.instanceOf(Headers).or(P.array()),
        (headers) => Object.fromEntries(headers)
      )
      .with(P.any, (headers) => headers)
      .exhaustive();

    if (tokenManager.accessToken)
      headers[HeadersEnum.Authorization] = `Bearer ${tokenManager.accessToken}`;
    init.headers = headers;

    appExternalStore.state.loading = true;
    return response => {
      const authenticated = match(response)
        .with(
          {
            url: P.when((url => {
              // but if we fail here then we know for sure we aren't authenticated
              return new URL(url).pathname.endsWith(TokenResource.pathname);
            })),
          },
          (response) => response.ok
        )
        .with({ status: HttpStatusCodes.Unauthorized }, () => false)
        .with(P.any, () => appExternalStore.state.authenticated)
        .exhaustive();
      
      appExternalStore.state.authenticated = authenticated;
      appExternalStore.state.loading = false;

      return response;
    };
  });

const TokenRefresh = async () => {
  const tokenManager = TokenManager.instance;
  if (tokenManager.refreshToken) {
    // refresh token available so let's try to
    // exchange for an access token
    const refresh_token = tokenManager.refreshToken;
    const tokenResult = await settle(
      queryClient.fetchQuery({
        queryKey: TOKEN_KEY,
        queryFn: () => {
          return token.post({
            refresh_token,
            grant_type: GrantType.RefreshToken,
            scope: PLAYER_DEFAULT_TOKEN_SCOPE,
          });
        },
      })
    );

    match(tokenResult)
      .with({ status: 'fulfilled', value: P.select() }, tokenResult => {
        tokenManager
          .setAccessToken(tokenResult.access_token, tokenResult.expires)
          .setRefreshToken(tokenResult.refresh_token);
      })
      .with({ status: 'rejected' }, () => {
        return;
      })
      .exhaustive();
  }
};

function addFetch<R extends Resource>(
  client: ResourceClientInstance<R>,
  fetch: typeof ResourceClient.fetch
) {
  client.fetch = fetch;
  return client;
}

export const attestation = addFetch(
  new AttestationResourceClient(AttestationResource),
  Fetch.build()
);
export const assertion = addFetch(
  new AssertionResourceClient(AssertionResource),
  Fetch.build()
);
export const token = addFetch(
  TokenResource.createClient(),
  Fetch.build()
);
export const user = addFetch(
  UserResource.createClient(),
  Fetch.with(TokenRefresh).build()
);
export const avatar = addFetch(
  AvatarResource.createClient(),
  Fetch.with(TokenRefresh).build()
);
export const game = addFetch(
  GameResource.createClient(),
  Fetch.with(TokenRefresh).build()
);
export const transactions = addFetch(
  TransactionsResource.createClient(),
  Fetch.with(TokenRefresh).build()
);
export const transactionRequests = addFetch(
  TransactionRequestsResource.createClient(),
  Fetch.with(TokenRefresh).build()
);
export const transactionRequestResponses = addFetch(
  TransactionRequestResponsesResource.createClient(),
  Fetch.with(TokenRefresh).build()
);
export const userAccount = addFetch(
  UserAccountResource.createClient(),
  Fetch.with(TokenRefresh).build()
);
export const systemAccounts = addFetch(
  SystemAccountsResource.createClient(),
  Fetch.with(TokenRefresh).build()
);
export const events = addFetch(
  EventsResource.createClient(),
  Fetch.with(TokenRefresh).build()
);

export const queryClient = new QueryClient();